import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { pxToRem, Button, Row, Col, SubHeading } from '@workramp/ui';
import XDate from 'xdate';
import { hideLoader, showLoader } from '../../common';
import NotificationsApi from '../../agent/notifications';
import Notification from './Notification';

const NotificationContent = ({ user, updateNavUnreadNotificationCount }) => {
  const TRANSLATED_ERROR_LOADING_NOTIFICATIONS = I18n.t(
    'js.learner.notifications.dropdown.error_loading_notifications',
  );
  const TRANSLATED_NO_NOTIFICATIONS = I18n.t('js.learner.notifications.dropdown.no_notifications');
  const TRANSLATED_HEADING = I18n.t('js.learner.notifications.dropdown.heading');
  const TRANSLATED_MARK_ALL_AS_READ = I18n.t('js.learner.notifications.dropdown.mark_all_as_read');
  const TRANSLATED_LOAD_MORE = I18n.t('js.learner.notifications.dropdown.load_more');

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [totalNotificationsCount, setTotalNotificationsCount] = useState(0);

  const getNotificationCount = () => {
    NotificationsApi.getNotificationCount(
      user.id,
      ({ unread, total }) => {
        updateNavUnreadNotificationCount?.(unread);
        setTotalNotificationsCount(total);
      },
      () => {
        // Do nothing on error, legacy decision
      },
    );
  };

  const loadNotifications = () => {
    if (isLoading) return;

    setIsLoading(true);
    NotificationsApi.loadNotifications(
      { id: user.id, offset: notifications.length },
      (newNotifications) => {
        const previousNotificationIds = notifications.map((n) => n.id);

        const newNotificationsDeduped = newNotifications.filter(
          (notification) => !previousNotificationIds.includes(notification.id),
        );
        const allNotifications = [...notifications, ...newNotificationsDeduped];

        setNotifications(allNotifications);
        setIsLoading(false);
        setLoadingError(null);
      },
      (err) => {
        setIsLoading(false);
        setLoadingError(err);
      },
    );
  };

  useEffect(() => {
    // get total Notification Count
    getNotificationCount();
    // get initial Notifications
    loadNotifications();
  }, []);

  const markAllRead = () => {
    showLoader();

    NotificationsApi.markAllNotificationsRead(
      user.id,
      () => {
        getNotificationCount();
        hideLoader();
        setNotifications(
          notifications.map((n) => ({
            ...n,
            hasBeenClicked: true,
          })),
        );
      },
      () => {
        // Do nothing on error, legacy decision
      },
    );
  };

  const filteredNotifications = notifications
    .filter((n) => n)
    .sort((a, b) => XDate(b.orderingTime).getTime() - XDate(a.orderingTime).getTime());

  return (
    <div>
      {loadingError && <Info>{TRANSLATED_ERROR_LOADING_NOTIFICATIONS}</Info>}
      {!loadingError && (
        <>
          <NotificationHeader>
            <Row align="center">
              <Col>
                <SubHeading level="lg">{TRANSLATED_HEADING}</SubHeading>
              </Col>
              {!!filteredNotifications.length && (
                <Col xs="auto">
                  <Button variant="text" size="lg" onPress={markAllRead}>
                    {TRANSLATED_MARK_ALL_AS_READ}
                  </Button>
                </Col>
              )}
            </Row>
          </NotificationHeader>
          <NotificationContentWrapper>
            {filteredNotifications.length === 0 && <Info>{TRANSLATED_NO_NOTIFICATIONS}</Info>}
            {filteredNotifications.map((notification) => (
              <Notification key={notification.id} notification={notification} />
            ))}
          </NotificationContentWrapper>
          {totalNotificationsCount > notifications.length && (
            <NotificationFooter>
              <Button variant="text" size="lg" isDisabled={isLoading} onPress={loadNotifications}>
                {TRANSLATED_LOAD_MORE}
              </Button>
            </NotificationFooter>
          )}
        </>
      )}
    </div>
  );
};

const NotificationHeader = styled.div`
  border-bottom: 1px solid rgba(151, 151, 151, 0.6);
  padding-left: ${({ theme }) => theme.spacing(4)}px;
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.surface[0]};
`;

const NotificationContentWrapper = styled.div``;

const NotificationFooter = styled.div`
  padding: ${({ theme }) => theme.spacing(1)}px;
  font-size: ${({ theme }) => pxToRem(theme.typography.generateFontSize(1))}rem;
  text-align: center;

  span {
    text-transform: none;
  }
`;

const Info = styled.div`
  padding: 8px;
  font-size: 14px;
`;

export default NotificationContent;
